import { z } from "zod";
import {
  FIELD_EMAIL_VALID,
  FIELD_REQUIRED,
} from "../../../i18n/keysTranslations";
import { ENTERPRISE_ADLE_MODULE_VALUES } from "../../../utils/constants";
import { ENTERPRISE_CREATIVES_BUFFER_NONE_VALUE } from "../config/enterpriseIntakeModuleFieldRegistry";
import {
  ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY,
  ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND,
  isEnterpriseIntakeAgency,
  wantsCreativesBuffer,
} from "../enterpriseIntakeFormValues";
import {
  isModuleSelected,
  isNonNegativeIntegerString,
  isPercentIntegerString,
  isPositiveIntegerString,
} from "../enterpriseIntakeModuleUtils";
import { asRadioString, asString, asStringArray } from "./schemaHelpers";

const addRequiredIssue = ({ ctx, path, t }) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: t(FIELD_REQUIRED),
    path: Array.isArray(path) ? path : [path],
  });
};

const validateRequiredPositiveInteger = ({ data, ctx, t, field }) => {
  if (!isPositiveIntegerString(data[field])) {
    addRequiredIssue({ ctx, path: field, t });
  }
};

const validateRequiredNonNegativeInteger = ({ data, ctx, t, field }) => {
  if (!isNonNegativeIntegerString(data[field])) {
    addRequiredIssue({ ctx, path: field, t });
  }
};

export const validateEnterpriseIntakeConditionalFields = ({ data, ctx, t }) => {
  const selectedModules = data.selectedAdleModules ?? [];

  if (!selectedModules.length) {
    addRequiredIssue({ ctx, path: "selectedAdleModules", t });
  }

  validateRequiredPositiveInteger({
    data,
    ctx,
    t,
    field: "scopeBrandConfigsCount",
  });
  validateRequiredPositiveInteger({
    data,
    ctx,
    t,
    field: "platformUsersCount",
  });

  if (!data.contractTermMonths?.trim()) {
    addRequiredIssue({ ctx, path: "contractTermMonths", t });
  }

  const hasCreativeAssets = isModuleSelected({
    selectedModules,
    moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.CREATIVE_ASSETS,
  });
  const hasCampaigns =
    isModuleSelected({
      selectedModules,
      moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_LAUNCH,
    }) ||
    isModuleSelected({
      selectedModules,
      moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_OPTIMIZATION,
    });
  const hasOptimizations = isModuleSelected({
    selectedModules,
    moduleValue: ENTERPRISE_ADLE_MODULE_VALUES.PAID_MEDIA_OPTIMIZATION,
  });

  if (hasCreativeAssets) {
    validateRequiredNonNegativeInteger({
      data,
      ctx,
      t,
      field: "module1MonthlyNewImagesCount",
    });
    validateRequiredNonNegativeInteger({
      data,
      ctx,
      t,
      field: "module1MonthlyImageAnimationsCount",
    });
    validateRequiredNonNegativeInteger({
      data,
      ctx,
      t,
      field: "module1MonthlyVideosCount",
    });

    if (
      !Array.isArray(data.creativesBufferTypes) ||
      data.creativesBufferTypes.length === 0
    ) {
      addRequiredIssue({ ctx, path: "creativesBufferTypes", t });
    } else if (wantsCreativesBuffer(data.creativesBufferTypes)) {
      if (!data.creativesBufferPercent?.trim()) {
        addRequiredIssue({ ctx, path: "creativesBufferPercent", t });
      } else if (data.creativesBufferPercent === "other") {
        if (!isPercentIntegerString(data.creativesBufferPercentOther)) {
          addRequiredIssue({ ctx, path: "creativesBufferPercentOther", t });
        }
      }
    } else if (
      data.creativesBufferTypes.length > 1 &&
      data.creativesBufferTypes.includes(ENTERPRISE_CREATIVES_BUFFER_NONE_VALUE)
    ) {
      addRequiredIssue({ ctx, path: "creativesBufferTypes", t });
    }
  }

  if (hasCampaigns) {
    validateRequiredNonNegativeInteger({
      data,
      ctx,
      t,
      field: "module2MonthlyCampaignsCount",
    });
  }

  if (hasOptimizations && !data.optimizationFrequency?.trim()) {
    addRequiredIssue({ ctx, path: "optimizationFrequency", t });
  }

  if (isEnterpriseIntakeAgency(data.organizationType)) {
    if (!data.whiteLabelRequirement?.trim()) {
      addRequiredIssue({ ctx, path: "whiteLabelRequirement", t });
    }
    return;
  }

  if (!data.mediaManagementAddon?.trim()) {
    addRequiredIssue({ ctx, path: "mediaManagementAddon", t });
  } else if (data.mediaManagementAddon === "yes") {
    validateRequiredNonNegativeInteger({
      data,
      ctx,
      t,
      field: "monthlyMediaSpendUsd",
    });
  }
};

/**
 * Creates the enterprise intake validation schema.
 */
export const createEnterpriseIntakeSchema = (t) =>
  z
    .object({
      organizationType: z.enum([
        ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY,
        ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND,
      ]),

      fullName: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) }),
      corporateEmail: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) })
        .email({ message: t(FIELD_EMAIL_VALID) }),
      companyName: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) }),
      jobTitle: z
        .string()
        .trim()
        .min(1, { message: t(FIELD_REQUIRED) }),

      selectedAdleModules: z.preprocess(asStringArray, z.array(z.string())),
      scopeBrandConfigsCount: z.preprocess(asString, z.string()),

      module1MonthlyNewImagesCount: z.preprocess(asString, z.string()),
      module1MonthlyImageAnimationsCount: z.preprocess(asString, z.string()),
      module1MonthlyVideosCount: z.preprocess(asString, z.string()),
      creativesBufferTypes: z.preprocess(asStringArray, z.array(z.string())),
      creativesBufferPercent: z.preprocess(asString, z.string()),
      creativesBufferPercentOther: z.preprocess(asString, z.string()),

      module2MonthlyCampaignsCount: z.preprocess(asString, z.string()),
      optimizationFrequency: z.preprocess(asRadioString, z.string()),

      platformUsersCount: z.preprocess(asString, z.string()),
      contractTermMonths: z.preprocess(asRadioString, z.string()),

      whiteLabelRequirement: z.preprocess(asRadioString, z.string()),

      mediaManagementAddon: z.preprocess(asRadioString, z.string()),
      monthlyMediaSpendUsd: z.preprocess(asString, z.string()),

      additionalNotes: z.preprocess(asString, z.string()),
    })
    .superRefine((data, ctx) => {
      validateEnterpriseIntakeConditionalFields({ data, ctx, t });
    });
